import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  facilityClock,
  formatTimeOfDay,
  toFacilityDate,
  WHOLE_DAY,
  windowForTime,
} from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalDay, optionalString, optionalTrue, KEEP_PLACE } from "@/lib/search";
import { PolicyScope, useCan } from "@/lib/policy";
import { FollowUpBanner } from "./-components/follow-up-banner";
import { PassHeaderActions } from "./-components/pass-header-actions";
import { RefillBanner } from "./-components/refill-banner";
import { ReviewBanner } from "./-components/review-banner";
import { ResidentPassScreen } from "./-components/pass/resident-pass-screen";
import { useWindowTallies } from "./-components/pass-windows";
import { RosterCard } from "./-components/roster-card";
import { RosterList } from "./-components/roster-list";
import { WindowStrip } from "./-components/window-strip";
import { PassSummary } from "./-components/pass-summary";

type PassSearch = { date?: string; window?: string; due?: true; everyone?: true };

export const Route = createFileRoute("/_authenticated/_pharmacy/med-pass/")({
  validateSearch: (search: Record<string, unknown>): PassSearch => ({
    date: optionalDay(search.date),
    window: optionalString(search.window)?.slice(0, 60) || undefined,
    due: optionalTrue(search.due),
    everyone: optionalTrue(search.everyone),
  }),
  component: MedPassPage,
});

function MedPassPage() {
  const queryClient = useQueryClient();
  const { facilityId: globalFacility } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<PassSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const date = params.date ?? "";
  const setDate = (next: string) => patch({ date: next || undefined });
  const [otherBuilding, setOtherBuilding] = useState(false);
  const windowId = otherBuilding ? null : (params.window ?? null);
  const setWindowId = (next: string) => {
    setOtherBuilding(false);
    patch({ window: next });
  };
  const dueFirst = Boolean(params.due);
  const setDueFirst = (update: (on: boolean) => boolean) =>
    patch({ due: update(dueFirst) ? true : undefined });
  const showEveryone = Boolean(params.everyone);
  const setShowEveryone = (update: boolean | ((on: boolean) => boolean)) =>
    patch({
      everyone: (typeof update === "function" ? update(showEveryone) : update) ? true : undefined,
    });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  // The pass runs at one building: the switcher's, or the only one there is.
  const facilityId =
    globalFacility !== "all"
      ? globalFacility
      : facilities.data?.length === 1
        ? (facilities.data[0]?.id ?? "")
        : "";
  const choosing = globalFacility === "all" && (facilities.data?.length ?? 0) > 1;
  // A window or resident from another building means nothing in this one.
  const [shownFacility, setShownFacility] = useState(facilityId);
  if (shownFacility !== facilityId) {
    setShownFacility(facilityId);
    if (shownFacility) setOtherBuilding(true);
    setSelectedId(null);
  }
  const canConfigure = useCan("settings:manage", facilityId || null);

  const windows = useQuery({
    ...orpc.pharmacy.medPass.windows.queryOptions({ input: { facilityId } }),
    enabled: Boolean(facilityId),
  });
  const rows = windows.data ?? [];
  const timezone = rows[0]?.timezone;
  const clock = timezone ? facilityClock(timezone) : "";
  const today = timezone ? toFacilityDate(new Date(), timezone) : "";
  const current = timezone ? windowForTime(clock, rows) : null;
  const windowSelection = windowId ?? current?.id ?? WHOLE_DAY;
  const wholeDay = windowSelection === WHOLE_DAY;
  const active = wholeDay ? undefined : rows.find((row) => row.id === windowSelection);
  const passDate = date || today;
  const from = active?.startTime ?? "00:00";
  const to = active?.endTime ?? "23:59";

  const roster = useQuery({
    ...orpc.pharmacy.medPass.roster.queryOptions({
      input: { facilityId, date: passDate, from, to },
    }),
    enabled: Boolean(facilityId) && Boolean(passDate) && rows.length > 0,
  });
  const tallies = useWindowTallies({ facilityId, date: passDate, today, clock, windows: rows });
  const range = roster.data?.range;
  /* Out of sight, out of mind: a resident with nothing scheduled in this window is not on this
   * round, so they are not on the list. A chip brings everyone back for the PRN that is not owed
   * but may be needed. */
  const everyone = roster.data?.residents ?? [];
  const people = everyone.filter(
    (person) => showEveryone || person.due > 0 || person.needsSchedule > 0,
  );
  // Room order from the server; "Due first" is a stable partition on top.
  const ordered = dueFirst
    ? [
        ...people.filter((person) => person.remaining > 0),
        ...people.filter((person) => person.remaining === 0),
      ]
    : people;
  const rangeLabel = active
    ? `${formatTimeOfDay(active.startTime)}–${formatTimeOfDay(active.endTime)}`
    : "";
  const selectedIndex = ordered.findIndex((person) => person.id === selectedId);
  const selected = selectedIndex >= 0 ? ordered[selectedIndex] : undefined;
  // Every window's roster, not only the one on screen: the strip counts them all.
  const refreshing = roster.isFetching || tallies.fetching;
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: orpc.pharmacy.medPass.roster.key() });

  if (choosing) {
    return (
      <div className="flex w-full flex-col gap-5">
        <PageHeader title="Med pass" />
        <OneBuildingCard prompt="Choose a building. The med pass runs per building." />
      </div>
    );
  }

  if (selected && range && facilityId) {
    const previous = ordered[selectedIndex - 1];
    const next = ordered[selectedIndex + 1];
    return (
      /* The pass's corrections and back-charting are asked at the building it runs at. */
      <PolicyScope facilityId={facilityId}>
        <ResidentPassScreen
          key={selected.id}
          resident={selected}
          facilityId={facilityId}
          date={passDate}
          range={range}
          windowLabel={active ? `${active.name}, ${rangeLabel}` : "Whole day"}
          windowed={!wholeDay}
          timezone={timezone ?? null}
          onBack={() => {
            setSelectedId(null);
            refresh();
          }}
          onPrev={previous ? () => setSelectedId(previous.id) : undefined}
          onNext={next ? () => setSelectedId(next.id) : undefined}
        />
      </PolicyScope>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <PageHeader
        title="Med pass"
        meta={
          rows.length > 0 && passDate ? (
            <PassSummary tallies={tallies} dayIsPast={passDate < today} />
          ) : null
        }
        actions={
          <PassHeaderActions
            facilityId={facilityId}
            date={passDate}
            onDate={setDate}
            refreshing={refreshing}
            onRefresh={refresh}
            windows={canConfigure ? rows : null}
          />
        }
      />

      {windows.isPending && facilityId ? (
        <div className="flex gap-2 overflow-hidden">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-24 min-w-[9.5rem] flex-1 rounded-lg" />
          ))}
        </div>
      ) : windows.isError ? (
        /* Without the windows the roster is never asked for, and its skeleton
           would stand in for a pass that is not coming. */
        <ReadFailed
          message="Couldn't load the pass windows, so the roster can't be built."
          error={windows.error}
          retrying={windows.isFetching}
          onRetry={() => void windows.refetch()}
        />
      ) : rows.length > 0 ? (
        <WindowStrip tallies={tallies} selected={windowSelection} onSelect={setWindowId} />
      ) : null}

      {facilityId ? (
        /* Each queue is absent when empty; so is the stack when all three are. */
        <div className="flex flex-col gap-3 empty:hidden">
          <ReviewBanner facilityId={facilityId} />
          <RefillBanner facilityId={facilityId} />
          <FollowUpBanner facilityId={facilityId} />
        </div>
      ) : null}

      {!facilityId && facilities.isSuccess ? (
        <EmptyState
          title="No facility available"
          description="Ask an administrator to assign you to a facility."
        />
      ) : windows.isError ? null : (
        <section aria-labelledby="pass-roster" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* The strip above already says which window is chosen. */}
            <h2 id="pass-roster" className="text-sm font-medium">
              Residents
              {roster.data ? (
                <span className="text-muted-foreground ml-1.5 font-normal tabular-nums">
                  {people.length}
                </span>
              ) : null}
            </h2>
            <fieldset className="flex min-w-0 flex-wrap gap-2">
              <legend className="sr-only">List options</legend>
              <FilterChip selected={dueFirst} onClick={() => setDueFirst((on) => !on)}>
                Due first
              </FilterChip>
              <FilterChip
                selected={showEveryone}
                count={roster.data ? everyone.length : undefined}
                onClick={() => setShowEveryone((on) => !on)}
              >
                All residents
              </FilterChip>
            </fieldset>
          </div>
          <RosterList
            state={
              roster.isPending
                ? "loading"
                : roster.isError
                  ? "failed"
                  : ordered.length
                    ? "ready"
                    : "empty"
            }
            failed={
              <ReadFailed
                message="Couldn't load the roster."
                error={roster.error}
                retrying={roster.isFetching}
                onRetry={() => void roster.refetch()}
              />
            }
            empty={
              everyone.length > 0 ? (
                <EmptyState
                  title="Nothing scheduled for anyone in this window"
                  description="Residents with nothing due are hidden. Show them all to give a PRN."
                  action={
                    <div className="flex flex-wrap justify-center gap-2">
                      {wholeDay ? null : (
                        <Button variant="outline" onClick={() => setWindowId(WHOLE_DAY)}>
                          Show the whole day
                        </Button>
                      )}
                      {showEveryone ? null : (
                        <Button variant="outline" onClick={() => setShowEveryone(true)}>
                          Show all residents
                        </Button>
                      )}
                    </div>
                  }
                />
              ) : (
                <EmptyState
                  title="No active residents at this facility"
                  description="Residents appear here once they are admitted to this building."
                />
              )
            }
          >
            {ordered.map((person) => (
              <RosterCard
                key={person.id}
                entry={person}
                pastDue={Math.min(person.remaining, tallies.pastDueFor(person.id, windowSelection))}
                onSelect={() => setSelectedId(person.id)}
              />
            ))}
          </RosterList>
        </section>
      )}
    </div>
  );
}
